import { IProof } from 'src/ts/interfaces';
import { FormValues, getEffectiveProofResult, OFFER_LETTER_TYPE } from '../helpers/proofHelpers';

export type OlvLockState =
  | 'pending_rulings'
  | 'blocked_edited'
  | 'blocked_terminal_ol'
  | 'unlocked';

interface UseOlvLockStateProps {
  proofs: IProof[];
  localFormData: Record<string, FormValues>;
  // ITL-2769: required so the hook can detect whether the displayed OL proof
  // is in cascade-failed terminal state (UNDETERMINED + CUSTOM). The
  // non-optional signature is a compile-time safety guarantee — call sites
  // that forget to pass it fail TypeScript, preventing silent "unlocked"
  // fallthrough on proofs that should be locked.
  currentProof: IProof | undefined;
}

export function useOlvLockState({
  proofs,
  localFormData,
  currentProof
}: UseOlvLockStateProps): OlvLockState {
  // ITL-2769: cascade-failed lock fires on ANY OL in a terminal hard-ruled
  // state — both the cascade target (sibling cascaded server-side) AND the
  // originating proof (which the analyst just submitted). Generic copy at
  // the overlay covers both cases; no need to distinguish them in the hook.
  //
  // Heuristic note: the BE writes result_insufficient_reason=:CUSTOM with
  // the actual reason codes in result_insufficient_reasons (plural). The
  // proof_view.ex:124-130 serializer FLATTENS this for backward compat —
  // [single_reason] becomes the scalar literal (e.g., "MISSING_COMPANY_NAME"),
  // [multiple_reasons] becomes "CUSTOM". So we cannot check for "CUSTOM"
  // literally — we check that the proof is in a terminal hard-ruled state
  // (UNDETERMINED with any reason OTHER than the soft-pending sentinel).
  if (
    currentProof &&
    (currentProof.type as string) === OFFER_LETTER_TYPE &&
    currentProof.result === 'UNDETERMINED' &&
    currentProof.result_insufficient_reason &&
    currentProof.result_insufficient_reason !== 'PENDING_EMPLOYER_VERIFICATION' &&
    currentProof.result_insufficient_reason !== 'NONE'
  ) {
    return 'blocked_terminal_ol';
  }

  const nonOfferLetterProofs = proofs.filter(
    (p) => (p.type as string) !== OFFER_LETTER_TYPE && !p.is_automatic_review
  );

  // Vacuous case: no other proofs to wait for
  if (nonOfferLetterProofs.length === 0) {
    return 'unlocked';
  }

  let allRuled = true;
  let anyEdited = false;

  for (const proof of nonOfferLetterProofs) {
    const effectiveResult = getEffectiveProofResult(proof, localFormData[proof.id]);

    if (effectiveResult === 'PENDING') {
      allRuled = false;
    }
    if (effectiveResult === 'EDITED') {
      anyEdited = true;
    }
  }

  if (anyEdited) return 'blocked_edited';
  if (!allRuled) return 'pending_rulings';
  return 'unlocked';
}
